import { EditOutlined, FileExcelOutlined, SearchOutlined } from "@ant-design/icons";
import { Button, Col, Input, notification, Row, Space, Spin, Table } from "antd";
import { ColumnType } from "antd/es/table";
import { useContext, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { utils as XLSXutils, writeFile as XLSXwriteFile } from "xlsx";
import { ContentBox } from "../../components/layout/content";
import { LoginedLayout } from "../../components/layout/loginLayout";
import { UserContext } from "../../context/userContext";
import { MasterCuttingData } from "../../Data/cutting";
import { GET } from "../../services";
import { UPLIFT_GET_ALL_CUTTING } from "../../services/path";
import AddMapCodeGModal from "./view/addMapCodeGModal";
import ImportExcelModal from "./view/importExcelModal";

interface cuttingResponse {
  current_page: number;
  page_size: number;
  total_record: number;
  total_page: number;
  master_cutting: MasterCuttingData[];
}

const CodeGPage = () => {
  const [searchParams, setSearchParams] = useState({
    searchTextFullCode: "",
    searchTextProductName: "",
    searchTextCuttingId: "",
  });

  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [searchUrlParams, setSearchUrlParams] = useSearchParams();
  const [searchbarValue, setSearchbarValue] = useState("");
  const [codeGData, setCodeGData] = useState<MasterCuttingData>({
    no: 0,
    sku: "",
    full_code: "",
    alter_sku: "",
    product_name: "",
    cutting_id: "",
    created_date: "",
    updated_date: "",
    updated_by: "",
    alter_name: "",
  });

  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<MasterCuttingData[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const tableRef = useRef<HTMLDivElement | null>(null);
  const { user } = useContext(UserContext);
  const handlePageChange = (page: number, size: number) => {
    let newSize;
    let newPage;
    if (page && !size) {
      newSize = pageSize;
      newPage = page;
    }

    if (!page && size) {
      newSize = size;
      newPage = 1;
    }

    if (page && size) {
      newSize = size;
      newPage = page;
    }

    setPageSize(newSize ? newSize : pageSize);
    setCurrentPage(page);
    updateSearchParams({
      current_page: (newPage ?? 1).toString(),
      page_size: (newSize ? newSize : pageSize).toString(),
    });
  };

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportExcelModalOpen, setIsImportExcelModalOpen] = useState(false);
  const pageOptions = ["10", "25", "50", "100"];
  const handleSearchInputChange = (field: string, value: string) => {
    setSearchParams({ ...searchParams, [field]: value });
    setCurrentPage(1);
  };

  const [originalOrders, setOriginalOrders] = useState<MasterCuttingData[]>([]);
  const loadData = (data: MasterCuttingData[]) => {
    setOriginalOrders(data); // Store original
    setOrders(data); // Display data
  };

  const initializeFromURL = () => {
    const pageSizeParam = searchUrlParams.get("page_size");
    if (!pageSizeParam || pageSizeParam === "NaN" || pageSizeParam === "") {
      setPageSize(25); // Default to 25 if not provided
    } else {
      setPageSize(parseInt(pageSizeParam)); // Set from URL if valid
    }

    const currentPageParam = searchUrlParams.get("current_page");
    if (!currentPageParam || currentPageParam === "NaN" || currentPageParam === "") {
      setCurrentPage(1); // Default to 1 if not provided
    } else {
      setCurrentPage(parseInt(currentPageParam)); // Set from URL if valid
    }

    const currentSearchBarInput = searchUrlParams.get("search");
    if (currentSearchBarInput) {
      setSearchbarValue(currentSearchBarInput);
    } else {
      setSearchbarValue("");
    }
  };

  // Update URL parameters helper function
  const updateSearchParams = (updates: Record<string, string | number>) => {
    setSearchUrlParams((prev) => {
      const newParams = new URLSearchParams(prev.toString());
      Object.entries(updates).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          newParams.set(key, value.toString());
        } else {
          newParams.delete(key);
        }
      });

      return newParams;
    });
  };

  const handleSearchBarChange = (e: any) => {
    setSearchbarValue(e.target.value);
  };

  // Updated useEffect
  useEffect(() => {
    if (
      !searchParams.searchTextFullCode &&
      !searchParams.searchTextProductName &&
      !searchParams.searchTextCuttingId
    ) {
      setOrders(originalOrders);
    } else {
      const filteredList = originalOrders.filter((item) => {
        const skuMatch = searchParams.searchTextFullCode
          ? item.full_code.toUpperCase().includes(searchParams.searchTextFullCode.toUpperCase())
          : true;

        const alterSkuMatch = searchParams.searchTextProductName
          ? item.product_name
              ?.toUpperCase()
              .includes(searchParams.searchTextProductName.toUpperCase())
          : true;

        const cuttingIdMatch = searchParams.searchTextCuttingId
          ? item.cutting_id // Use cutting_id as cutting id
              ?.toString()
              .includes(searchParams.searchTextCuttingId)
          : true;

        return skuMatch && alterSkuMatch && cuttingIdMatch;
      });

      setOrders(filteredList);
    }
  }, [searchParams, originalOrders]);

  const fetchOrdersData = async (searchBarValue?: string) => {
    setLoading(true);
    try {
      let result: cuttingResponse;
      if (searchBarValue) {
        result = await GET(
          UPLIFT_GET_ALL_CUTTING(pageSize.toString(), currentPage.toString(), searchBarValue),
        );
      } else {
        result = await GET(UPLIFT_GET_ALL_CUTTING(pageSize.toString(), currentPage.toString()));
      }

      if (result && result.total_record > 0) {
        setTotalCount(result.total_record);
        loadData(result.master_cutting); // Use loadData instead of setOrders
      } else {
        notification.warning({
          message: "ไม่มีข้อมูลในแบทช์.",
          duration: 2,
        });

        setOrders([]);
        setOriginalOrders([]);
      }
    } catch (error) {
      notification.error({
        message: "เกิดข้อผิดพลาดในการดึงข้อมูล.",
        description: "กรุณาลองใหม่อีกครั้ง",
        duration: 2,
      });

      setOrders([]);
      setOriginalOrders([]);
    } finally {
      setLoading(false);
    }
  };

  /// init param from url
  useEffect(() => {
    initializeFromURL();
  }, [isModalOpen, isImportExcelModalOpen]);

  //when first mount kub!
  useEffect(() => {
    const initializeData = async () => {
      await fetchOrdersData(searchbarValue);
    };

    initializeData();
  }, [currentPage, pageSize, isModalOpen, isImportExcelModalOpen]);

  const searchInput = (
    field: string,
    placeHolder?: string,
    valueLocal?: string | number | readonly string[] | undefined,
  ) => {
    return (
      <Input
        placeholder={placeHolder}
        value={valueLocal}
        onChange={(e) => handleSearchInputChange(field, e.target.value)}
        onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
        style={{ width: 200 }}
        suffix={<SearchOutlined style={{ color: "gray" }} />}
      />
    );
  };

  useEffect(() => {
    if (tableRef.current) {
      const table = tableRef.current;
      const isOverflowing = table.scrollWidth > table.clientWidth;
      table.style.overflowX = isOverflowing ? "scroll" : "auto";
    }
  }, [orders]);

  const cellTitle = (
    title: string,
    field: string,
    placeHolder?: string,
    valueLocal?: string | number | readonly string[] | undefined,
  ): React.ReactNode => {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          alignContent: "center",
        }}
      >
        <span
          style={{
            flexBasis: "100%",
            width: "100px",
            maxWidth: "100px",
          }}
        >
          {title} :
        </span>
        {searchInput(field, placeHolder, valueLocal)}
      </div>
    );
  };

  const cellSpan = (text: string, isCut?: boolean) => {
    return (
      <div>
        <span>{text}</span>
      </div>
    );
  };

  const handleOnItemClick = async (record: MasterCuttingData) => {
    setCodeGData(record);
    setIsModalOpen(true);
  };

  const downloadCodeGTemplate = () => {
    const ws = XLSXutils.json_to_sheet([
      { SKU: "", Alter_SKU: "", Alter_Name: "", Cutting_ID: "" },
    ]);

    const wb = XLSXutils.book_new();
    XLSXutils.book_append_sheet(wb, ws, "Template");
    XLSXwriteFile(wb, "CodeGtemplate.xlsx");
  };

  const hnandleOnDownloadTemplate = async () => {
    setIsLoadingTemplate(true);
    downloadCodeGTemplate();
    notification.success({
      message: "ดาวน์โหลดเทมเพลตสำเร็จ",
      duration: 2,
    });

    setIsLoadingTemplate(false);
  };

  const columns: ColumnType<MasterCuttingData>[] = [
    {
      title: cellTitle(
        "รหัสสินค้า",
        "searchTextFullCode",
        "Search SKU",
        searchParams.searchTextFullCode,
      ),
      dataIndex: "full_code",
      key: "full_code",
      sorter: (a: MasterCuttingData, b: MasterCuttingData) =>
        a.full_code.localeCompare(b.full_code),
      render: (text: string) => cellSpan(text),
    },
    {
      title: cellTitle(
        "ชื่อสินค้า",
        "searchTextProductName",
        "Search Name",
        searchParams.searchTextProductName,
      ),
      dataIndex: "product_name",
      key: "product_name",
      sorter: (a: MasterCuttingData, b: MasterCuttingData) =>
        a.alter_sku.localeCompare(b.alter_sku),
      render: (text: string) => cellSpan(text),
    },
    {
      title: cellTitle(
        "ID เครื่องปริ้น",
        "searchTextCuttingId",
        "Search cutting ID",
        searchParams.searchTextCuttingId,
      ),
      dataIndex: "cutting_id",
      key: "cutting_id",
      sorter: (a: MasterCuttingData, b: MasterCuttingData) =>
        a.cutting_id.localeCompare(b.cutting_id),
      render: (text: string) => cellSpan(text),
    },
    {
      title: "Action",
      key: "cut",
      render: (text: string, record: MasterCuttingData) => (
        <Button onClick={() => handleOnItemClick(record)} icon={<EditOutlined />} type="primary">
          แก้ไขข้อมูล
        </Button>
      ),
    },
  ];

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleGetMasterCuttingFromSearchBar = async (value: string) => {
    setLoading(true);
    updateSearchParams({
      search: value,
      current_page: "1",
      page_size: pageSize.toString(),
    });

    await fetchOrdersData(value);
  };

  return (
    <LoginedLayout>
      <ContentBox>
        {loading && <Spin size="large" style={{ position: "absolute", top: "50%", left: "50%" }} />}
        <div
          style={{
            marginBottom: "16px",
            borderBottom: "1px solid #e8e8e8",
            paddingBottom: "8px",
          }}
        >
          <h2 style={{ marginBottom: "8px" }}>Code G</h2>
        </div>
        <Row
          style={{
            margin: "40px 0px",
            display: "flex",
            alignItems: "center",
            gap: "16px",
            flexDirection: "row-reverse",
          }}
        >
          <Col>
            <Button
              type="primary"
              className="ant-btn-color-green"
              icon={<FileExcelOutlined />}
              onClick={() => setIsImportExcelModalOpen(true)}
            >
              Import Excel
            </Button>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<FileExcelOutlined />}
              onClick={hnandleOnDownloadTemplate}
              loading={isLoadingTemplate}
            >
              Download Template Excel
            </Button>
          </Col>
        </Row>
        <Row
          style={{
            margin: "20px 0px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Space
            style={{
              width: "100vh",
              overflow: "auto",
              flexWrap: "wrap",
            }}
          >
            <Input.Search
              placeholder="Search for SKU, Name, Cutting ID"
              onSearch={handleGetMasterCuttingFromSearchBar}
              onChange={handleSearchBarChange} // Update state on input change
              value={searchbarValue}
              style={{ flex: 1, width: "75vh" }}
            />
          </Space>
        </Row>
        <div ref={tableRef}>
          <Table
            columns={columns}
            dataSource={orders}
            pagination={{
              position: ["bottomRight"],
              pageSize: pageSize,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              current: currentPage,
              onChange: (page, size) => handlePageChange(page, size),
              total: totalCount,
              showQuickJumper: true,
              pageSizeOptions: pageOptions,
            }}
            scroll={{ x: 1500 }}
          />
        </div>
        <div className="flex justify-center items-center">
          <AddMapCodeGModal
            open={isModalOpen}
            onCancel={handleCancel}
            width="40vw"
            centered // Add this prop to center the modal
            bodyStyle={{
              padding: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "35vh",
            }}
            sku={codeGData.sku}
            alter_sku={codeGData.alter_sku}
            alter_name={codeGData.alter_name}
            cutting_id={codeGData.cutting_id}
            visible={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            user={user}
          />
        </div>
        <div className="flex justify-center items-center">
          <ImportExcelModal
            open={isImportExcelModalOpen}
            onCancel={() => setIsImportExcelModalOpen(false)}
            width="40vw"
            centered // Add this prop to center the modal
            bodyStyle={{
              padding: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "35vh",
              flexDirection: "column",
            }}
            onClose={() => setIsImportExcelModalOpen(false)}
            user={user}
          />
        </div>
      </ContentBox>
    </LoginedLayout>
  );
};

export default CodeGPage;
